import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bot, FileText, Globe, FileUp, Settings, Sparkles, Zap,
  ChevronLeft, ChevronRight, Check, SkipForward, X,
  Upload, Plus, Trash2, Building2, Image
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';

const WIZARD_STEPS = [
  { id: 'basic', title: 'Basic Info', icon: Bot, description: 'Name and logo' },
  { id: 'business', title: 'Business Details', icon: Building2, description: 'Summary and scope' },
  { id: 'context', title: 'Context Sources', icon: FileText, description: 'Add knowledge sources' },
  { id: 'response', title: 'Response Settings', icon: Settings, description: 'Style and variants' },
  { id: 'google', title: 'Google Profile', icon: Globe, description: 'Business enrichment' },
  { id: 'buttons', title: 'Quick Buttons', icon: Zap, description: 'Starter prompts' },
];

export default function AgentWizard({ 
  isOpen, 
  onClose, 
  agentId = null, 
  onComplete,
  initialStep = 0 
}) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // Agent data
  const [agent, setAgent] = useState({
    name: '',
    logo: null,
    preferred_role: '',
    business_summary: '',
    out_of_scope_message: "I can only help with questions about {TENANT_NAME}. Please ask about our services, hours, pricing, or policies.",
    context_text: "You are a helpful AI assistant.",
    response_style: 'normal',
    prompt_variant: 'A',
    enable_google_enrichment: false,
    business_name_public: '',
    business_address_query: '',
  });
  
  // Context sources
  const [contextSources, setContextSources] = useState([]);
  const [newPlainText, setNewPlainText] = useState({ name: '', content: '' });
  const [newWebsite, setNewWebsite] = useState({ name: '', url: '' });
  
  // Quick buttons
  const [quickButtons, setQuickButtons] = useState([]);
  const [newButton, setNewButton] = useState({ label: '', prompt_text: '' });
  
  // Edit mode - load existing agent data
  useEffect(() => {
    if (isOpen && agentId) {
      loadAgentData();
    } else if (isOpen && !agentId) {
      // Reset for new agent
      setAgent({
        name: '',
        logo: null,
        preferred_role: '',
        business_summary: '',
        out_of_scope_message: "I can only help with questions about {TENANT_NAME}. Please ask about our services, hours, pricing, or policies.",
        context_text: "You are a helpful AI assistant.",
        response_style: 'normal',
        prompt_variant: 'A',
        enable_google_enrichment: false,
        business_name_public: '',
        business_address_query: '',
      });
      setContextSources([]);
      setQuickButtons([]);
      setCurrentStep(0);
    }
  }, [isOpen, agentId]);

  const loadAgentData = async () => {
    setLoading(true);
    try {
      const [agentRes, contextRes, buttonsRes] = await Promise.all([
        api.get(`/agents/${agentId}`),
        api.get(`/agents/${agentId}/context-sources`),
        api.get(`/agents/${agentId}/buttons`)
      ]);
      
      setAgent(agentRes.data);
      setContextSources(contextRes.data.sources || []);
      setQuickButtons(buttonsRes.data || []);
    } catch (error) {
      toast.error('Failed to load agent data');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setAgent(prev => ({ ...prev, [field]: value }));
  };

  const saveAgent = async () => {
    setSaving(true);
    try {
      if (agentId) {
        // Update existing agent
        await api.put(`/agents/${agentId}`, {
          name: agent.name,
          preferred_role: agent.preferred_role,
          business_summary: agent.business_summary,
          out_of_scope_message: agent.out_of_scope_message,
          context_text: agent.context_text,
          response_style: agent.response_style,
          prompt_variant: agent.prompt_variant,
          enable_google_enrichment: agent.enable_google_enrichment,
          business_name_public: agent.business_name_public,
          business_address_query: agent.business_address_query,
        });
        return agentId;
      } else {
        // Create new agent
        const response = await api.post('/agents', {
          name: agent.name,
          preferred_role: agent.preferred_role,
          context_text: agent.context_text,
          business_summary: agent.business_summary,
          out_of_scope_message: agent.out_of_scope_message,
          response_style: agent.response_style,
        });
        return response.data.agent_id;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save agent');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Validate current step
    if (currentStep === 0 && !agent.name.trim()) {
      toast.error('Agent name is required');
      return;
    }
    
    // Save agent on first step if creating new
    if (currentStep === 0 && !agentId) {
      try {
        const newAgentId = await saveAgent();
        // Update the component to use the new agent ID
        if (onComplete) {
          onComplete(newAgentId, false); // false = not finished yet
        }
      } catch {
        return;
      }
    }
    
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = async () => {
    try {
      const savedAgentId = await saveAgent();
      toast.success(agentId ? 'Agent updated successfully!' : 'Agent created successfully!');
      if (onComplete) {
        onComplete(savedAgentId, true); // true = finished
      }
      onClose();
    } catch {
      // Error already handled in saveAgent
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !agentId) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post(`/agents/${agentId}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAgent(prev => ({ ...prev, logo: response.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  // Context source handlers
  const handleAddPlainText = async () => {
    if (!newPlainText.name.trim() || !newPlainText.content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    
    const targetAgentId = agentId || agent.agent_id;
    if (!targetAgentId) {
      toast.error('Please complete the first step first');
      return;
    }
    
    try {
      const response = await api.post(`/agents/${targetAgentId}/context-sources`, {
        source_type: 'plain_text',
        name: newPlainText.name,
        content: newPlainText.content
      });
      setContextSources([...contextSources, response.data.source]);
      setNewPlainText({ name: '', content: '' });
      toast.success('Plain text context added');
    } catch (error) {
      toast.error('Failed to add context');
    }
  };

  const handleAddWebsite = async () => {
    if (!newWebsite.name.trim() || !newWebsite.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    
    const targetAgentId = agentId || agent.agent_id;
    if (!targetAgentId) {
      toast.error('Please complete the first step first');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post(`/agents/${targetAgentId}/context-sources`, {
        source_type: 'website',
        name: newWebsite.name,
        url: newWebsite.url
      });
      setContextSources([...contextSources, response.data.source]);
      setNewWebsite({ name: '', url: '' });
      
      if (response.data.source.crawl_status === 'success') {
        toast.success(response.data.message);
      } else {
        toast.warning('Website added but crawl failed. You can retry later.');
      }
    } catch (error) {
      toast.error('Failed to add website');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContextSource = async (sourceId) => {
    try {
      await api.delete(`/context-sources/${sourceId}`);
      setContextSources(contextSources.filter(s => s.source_id !== sourceId));
      toast.success('Context source deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Quick button handlers
  const handleAddQuickButton = async () => {
    if (!newButton.label.trim() || !newButton.prompt_text.trim()) {
      toast.error('Label and prompt text are required');
      return;
    }
    
    const targetAgentId = agentId || agent.agent_id;
    if (!targetAgentId) {
      toast.error('Please complete the first step first');
      return;
    }
    
    try {
      const response = await api.post(`/agents/${targetAgentId}/buttons`, {
        label: newButton.label,
        prompt_text: newButton.prompt_text,
        sort_order: quickButtons.length
      });
      setQuickButtons([...quickButtons, response.data]);
      setNewButton({ label: '', prompt_text: '' });
      toast.success('Quick button added');
    } catch (error) {
      toast.error('Failed to add button');
    }
  };

  const handleDeleteQuickButton = async (buttonId) => {
    try {
      await api.delete(`/buttons/${buttonId}`);
      setQuickButtons(quickButtons.filter(b => b.button_id !== buttonId));
      toast.success('Button deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Info
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Agent Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Customer Support Agent"
                value={agent.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="h-11"
                data-testid="wizard-agent-name"
              />
              <p className="text-xs text-muted-foreground">This name is displayed to users in the chat</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="preferred_role" className="text-sm font-medium">Preferred Agent Role</Label>
              <Input
                id="preferred_role"
                placeholder="e.g., Customer Support Specialist, Sales Representative"
                value={agent.preferred_role || ''}
                onChange={(e) => handleChange('preferred_role', e.target.value)}
                className="h-11"
                data-testid="wizard-preferred-role"
              />
              <p className="text-xs text-muted-foreground">Sets the agent's persona (e.g., "You are a...")</p>
            </div>
            
            {agentId && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Agent Logo</Label>
                <div className="flex items-center gap-4">
                  {agent.logo ? (
                    <img src={agent.logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Image className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Logo
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="context" className="text-sm font-medium">System Prompt</Label>
              <Textarea
                id="context"
                placeholder="Describe the agent's role..."
                value={agent.context_text}
                onChange={(e) => handleChange('context_text', e.target.value)}
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">Defines how the agent behaves</p>
            </div>
          </div>
        );

      case 1: // Business Details
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="business_summary" className="text-sm font-medium">Business Summary</Label>
              <Textarea
                id="business_summary"
                placeholder="Describe your business, what you do, and what you offer..."
                value={agent.business_summary || ''}
                onChange={(e) => handleChange('business_summary', e.target.value)}
                className="min-h-[120px]"
                data-testid="wizard-business-summary"
              />
              <p className="text-xs text-muted-foreground">Helps the AI understand your business context</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="out_of_scope" className="text-sm font-medium">Out of Scope Message</Label>
              <Textarea
                id="out_of_scope"
                placeholder="Message shown when user asks about topics outside your business..."
                value={agent.out_of_scope_message || ''}
                onChange={(e) => handleChange('out_of_scope_message', e.target.value)}
                className="min-h-[80px]"
              />
              <p className="text-xs text-muted-foreground">Use {'{TENANT_NAME}'} to insert your business name</p>
            </div>
          </div>
        );

      case 2: // Context Sources
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Add additional knowledge sources to help your agent answer questions accurately.
            </p>
            
            {/* Plain Text */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <h4 className="font-medium">Plain Text</h4>
                  <Badge variant="secondary" className="ml-auto">
                    {contextSources.filter(s => s.source_type === 'plain_text').length} added
                  </Badge>
                </div>
                <div className="space-y-3">
                  <Input
                    placeholder="Name (e.g., FAQ)"
                    value={newPlainText.name}
                    onChange={(e) => setNewPlainText({ ...newPlainText, name: e.target.value })}
                  />
                  <Textarea
                    placeholder="Enter context content..."
                    value={newPlainText.content}
                    onChange={(e) => setNewPlainText({ ...newPlainText, content: e.target.value })}
                    className="min-h-[80px]"
                  />
                  <Button onClick={handleAddPlainText} size="sm" disabled={!agentId && !agent.agent_id}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                
                {contextSources.filter(s => s.source_type === 'plain_text').map(source => (
                  <div key={source.source_id} className="flex items-center justify-between mt-2 p-2 bg-slate-50 rounded">
                    <span className="text-sm">{source.name}</span>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteContextSource(source.source_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Websites */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-5 h-5 text-blue-500" />
                  <h4 className="font-medium">Websites</h4>
                  <Badge variant="secondary" className="ml-auto">
                    {contextSources.filter(s => s.source_type === 'website').length} added
                  </Badge>
                </div>
                <div className="space-y-3">
                  <Input
                    placeholder="Name (e.g., Homepage)"
                    value={newWebsite.name}
                    onChange={(e) => setNewWebsite({ ...newWebsite, name: e.target.value })}
                  />
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={newWebsite.url}
                    onChange={(e) => setNewWebsite({ ...newWebsite, url: e.target.value })}
                  />
                  <Button onClick={handleAddWebsite} size="sm" disabled={saving || (!agentId && !agent.agent_id)}>
                    {saving ? 'Crawling...' : <><Plus className="w-4 h-4 mr-1" /> Add & Crawl</>}
                  </Button>
                </div>
                
                {contextSources.filter(s => s.source_type === 'website').map(source => (
                  <div key={source.source_id} className="flex items-center justify-between mt-2 p-2 bg-slate-50 rounded">
                    <div>
                      <span className="text-sm font-medium">{source.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {source.crawl_status === 'success' ? '✓ Crawled' : '⚠ Failed'}
                      </span>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteContextSource(source.source_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {(!agentId && !agent.agent_id) && (
              <p className="text-sm text-amber-600">
                Complete Step 1 first to add context sources.
              </p>
            )}
          </div>
        );

      case 3: // Response Settings
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Response Style</Label>
              <Select
                value={agent.response_style}
                onValueChange={(value) => handleChange('response_style', value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short (Brief, concise responses)</SelectItem>
                  <SelectItem value="normal">Normal (Detailed, comprehensive responses)</SelectItem>
                  <SelectItem value="fun">Fun (Engaging with emojis, links & images) 🎉</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "Fun" style includes emojis and shows relevant links and images from your context sources inline.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Prompt Variant (A/B Testing)</Label>
              <Select
                value={agent.prompt_variant || 'A'}
                onValueChange={(value) => handleChange('prompt_variant', value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">Variant A (Guardrails First)</SelectItem>
                  <SelectItem value="B">Variant B (Context First)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Test different prompt orderings to optimize response quality
              </p>
            </div>
          </div>
        );

      case 4: // Google Profile
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <h4 className="font-medium">Enable Google Business Profile</h4>
                <p className="text-sm text-muted-foreground">
                  Enrich your agent with verified business data from Google
                </p>
              </div>
              <Switch
                checked={agent.enable_google_enrichment}
                onCheckedChange={(checked) => handleChange('enable_google_enrichment', checked)}
              />
            </div>
            
            {agent.enable_google_enrichment && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Business Name (as on Google)</Label>
                  <Input
                    placeholder="e.g., Acme Corp"
                    value={agent.business_name_public || ''}
                    onChange={(e) => handleChange('business_name_public', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Address / City</Label>
                  <Input
                    placeholder="e.g., 123 Main St, San Francisco, CA"
                    value={agent.business_address_query || ''}
                    onChange={(e) => handleChange('business_address_query', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Used to find your business on Google Maps
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 5: // Quick Buttons
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Add quick action buttons that appear in the chat interface to help users get started.
            </p>
            
            <Card>
              <CardContent className="pt-4 space-y-3">
                <Input
                  placeholder="Button Label (e.g., 'Get Pricing')"
                  value={newButton.label}
                  onChange={(e) => setNewButton({ ...newButton, label: e.target.value })}
                />
                <Textarea
                  placeholder="Prompt Text (what gets sent when clicked)"
                  value={newButton.prompt_text}
                  onChange={(e) => setNewButton({ ...newButton, prompt_text: e.target.value })}
                  className="min-h-[60px]"
                />
                <Button onClick={handleAddQuickButton} size="sm" disabled={!agentId && !agent.agent_id}>
                  <Plus className="w-4 h-4 mr-1" /> Add Button
                </Button>
              </CardContent>
            </Card>
            
            {quickButtons.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Added Buttons</Label>
                {quickButtons.map(button => (
                  <div key={button.button_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium text-sm">{button.label}</span>
                      <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                        {button.prompt_text}
                      </p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteQuickButton(button.button_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {(!agentId && !agent.agent_id) && (
              <p className="text-sm text-amber-600">
                Complete Step 1 first to add quick buttons.
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="agent-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {agentId ? 'Edit Agent' : 'Create New Agent'}
          </DialogTitle>
          <DialogDescription>
            {WIZARD_STEPS[currentStep].description}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="flex items-center gap-1 py-4">
          {WIZARD_STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(index)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    isCurrent 
                      ? 'bg-primary text-white' 
                      : isCompleted 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-slate-100 text-slate-400'
                  }`}
                  disabled={index > currentStep && !agentId}
                >
                  <StepIcon className="w-4 h-4" />
                  <span className="text-xs font-medium hidden sm:inline">{step.title}</span>
                </button>
                {index < WIZARD_STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-primary' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto py-4 px-1">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <DialogFooter className="flex items-center justify-between border-t pt-4">
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleSkip}>
              <SkipForward className="w-4 h-4 mr-1" />
              Skip
            </Button>
            
            {currentStep < WIZARD_STEPS.length - 1 ? (
              <Button onClick={handleNext} disabled={saving}>
                {saving ? 'Saving...' : 'Next'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleFinish} disabled={saving}>
                {saving ? 'Saving...' : 'Finish'}
                <Check className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
